import type React from "react"
import { useTranslation } from "react-i18next"

import { MeterCodeField } from "@/components/fields/meter-code-field"
import { ResourceField } from "@/components/fields/resource-field"
import type { Meter } from "@/models/meter"
import { buildResourceFilterOptions } from "@/services/bill/bill-resource"
import type { DateTime } from "luxon"

import { Skeleton, Stack } from "@mui/material"
import type { DateRange } from "@mui/x-date-pickers-pro/models"

import { SiteBillsDateRange } from "./site-bills-date-range"

const FILTER_SKELETON_HEIGHT = 40

interface SiteBillsFiltersProps {
  anchorEnd: string | null
  dateRange: { end: string; start: string } | null
  isLoading: boolean
  meters: readonly Meter[]
  onDateRangeChange: (range: DateRange<DateTime>) => void
  onMeterChange: (meter: Meter | null) => void
  onResourceChange: (resource: string) => void
  resourceCodes: readonly string[]
  resourceType: string | null
  selectedMeter: Meter | null
}

export const SiteBillsFilters: React.FC<SiteBillsFiltersProps> = ({
  anchorEnd,
  dateRange,
  isLoading,
  meters,
  onDateRangeChange,
  onMeterChange,
  onResourceChange,
  resourceCodes,
  resourceType,
  selectedMeter,
}) => {
  const { t: tCommon } = useTranslation("common")
  const resourceOptions = buildResourceFilterOptions(resourceCodes, (key) =>
    tCommon(key)
  )

  if (isLoading) {
    return (
      <Skeleton
        aria-label={tCommon("loadingAriaLabel", {
          title: tCommon("fields.resource.title"),
        })}
        height={FILTER_SKELETON_HEIGHT}
        variant="rounded"
        width="100%"
      />
    )
  }

  return (
    <Stack
      direction="row"
      sx={{ alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}
    >
      <ResourceField
        disabled={resourceOptions.length === 0}
        onChange={onResourceChange}
        options={resourceOptions}
        value={resourceType ?? ""}
      />
      {dateRange && anchorEnd ? (
        <SiteBillsDateRange
          anchorEnd={anchorEnd}
          end={dateRange.end}
          onDateRangeChange={onDateRangeChange}
          start={dateRange.start}
        />
      ) : null}
      {meters.length > 0 ? (
        <MeterCodeField
          meters={meters}
          onChange={onMeterChange}
          value={selectedMeter}
        />
      ) : null}
    </Stack>
  )
}
